// src/pages/Income.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useLookups } from '../context/LookupContext';
import { useAppContext } from '../context/AppContext';
import { deleteIncomeRecord, fetchIncomesData, saveOrUpdateIncomeRecord } from '../services/incomeService';
import { fetchDependents } from '../services/assetService';
import { LookupValue } from '../services/lookupService';
import { RefreshCw } from 'lucide-react';

// Import your new components
import Notification from '../components/shared/Notification';
import IncomeHeader from '../components/income/IncomeHeader';
import IncomeForm from '../components/income/IncomeForm';
import ActionBar from '../components/income/ActionBar';
import IncomesTable from '../components/income/IncomesTable';
import IncomesMobileView from '../components/income/IncomesMobileView';
import Pagination from '../components/shared/Pagination';
import EmptyState from '../components/income/EmptyState';

// Interface is correct
export interface IncomeData {
  id: string;
  incomeOwnerType: string;
  incomeType: string;
  incomeSubType: string;
  incomeName: string;
  incomeAmount: string;
  incomeFrequency: string;
  totalIncomeAmount: string;
  totalFamilyIncome: string;
  status: string;
  familyMemberId?: string;
}

export type ColumnKey = keyof IncomeData | 'ownerTypeLabel' | 'typeLabel' | 'subTypeLabel' | 'frequencyLabel';

const visibleColumns: ColumnKey[] = [
  'incomeOwnerType', 'incomeName', 'incomeType', 'incomeSubType',
  'incomeAmount', 'incomeFrequency', 'totalIncomeAmount',
];

const getColumnDisplayName = (col: ColumnKey): string => {
  const displayNames: Record<ColumnKey, string> = {
    id: 'ID', incomeOwnerType: 'Owner Type', incomeType: 'Type', incomeSubType: 'Sub Type',
    incomeName: 'Name', incomeAmount: 'Amount', incomeFrequency: 'Frequency',
    totalIncomeAmount: 'Total Annual Income', totalFamilyIncome: 'Total Family Income',
    status: 'Status', familyMemberId: '', ownerTypeLabel: 'Owner Type',
    typeLabel: 'Type', subTypeLabel: 'Sub Type', frequencyLabel: 'Frequency',
  };
  return displayNames[col];
};

const Income: React.FC = () => {
    const { state } = useAppContext();
    const { getOptions, getLabel, isLoading: isLookupsLoading } = useLookups();

    const [transformedData, setTransformedData] = useState<IncomeData[]>([]);
    const [dependents, setDependents] = useState<{ id: string; name: string }[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [apiLoading, setApiLoading] = useState(true);
    const [operationStatus, setOperationStatus] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });
    const [formData, setFormData] = useState<IncomeData>({
        id: '', incomeOwnerType: '', incomeType: '', incomeSubType: '', incomeName: '', incomeAmount: '',
        incomeFrequency: '', totalIncomeAmount: '', totalFamilyIncome: '', status: '', familyMemberId: ''
    });
    const [dropdownOptions, setDropdownOptions] = useState<{ ownerType: LookupValue[]; incomeType: LookupValue[]; incomeSubType: LookupValue[]; incomeFrequency: LookupValue[]; }>({
        ownerType: [], incomeType: [], incomeSubType: [], incomeFrequency: [],
    });
    const [loadingSubTypes, setLoadingSubTypes] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: ColumnKey; direction: 'asc' | 'desc' | null; }>({ key: 'incomeName', direction: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState<Partial<Record<ColumnKey, string>>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);

    const lookupGroupMap: Record<string, string> = {
        incomeOwnerType: 'ownerType',
        incomeType: 'incomeType',
        incomeSubType: 'incomeSubType',
        incomeFrequency: 'incomeFrequency',
    };

    const formatCurrency = useCallback((value: string | number) => {
        const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
        if (isNaN(num)) return 'N/A';
        const symbol = state.currencySymbol || localStorage.getItem('currencySymbol') || '₹';
        return `${symbol}${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.floor(num))}`;
    }, [state.currencySymbol]);

    const resetForm = () => {
        setFormData({
            id: '', incomeOwnerType: '', incomeType: '', incomeSubType: '', incomeName: '', incomeAmount: '',
            incomeFrequency: '', totalIncomeAmount: '', totalFamilyIncome: '', status: '', familyMemberId: ''
        });
        setEditingId(null);
    };

    const fetchIncomeFromAPI = useCallback(async () => {
        setApiLoading(true);
        try {
            const apiData = await fetchIncomesData();
            if (!apiData || !Array.isArray(apiData.data)) throw new Error('Invalid API response structure');
            
            const rawIncomes = apiData.data.map((item: any): IncomeData => ({
                id: item.id || '', status: item.status || 'active', incomeOwnerType: item.incomeOwnerType || '',
                incomeType: item.incomeType || '', incomeSubType: item.incomeSubType || '', incomeName: item.incomeName || '',
                incomeAmount: item.incomeAmount || '', incomeFrequency: item.incomeFrequency || '',
                totalIncomeAmount: item.totalIncomeAmount || '', totalFamilyIncome: item.totalFamilyIncome || '',
                familyMemberId: item.familyMemberId || '',
            }));
            setTransformedData(rawIncomes);
        } catch (error) {
            console.error('Failed to fetch income data:', error);
            setOperationStatus({ show: true, message: 'Failed to fetch income. Check console.', type: 'error' });
        } finally {
            setApiLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchIncomeFromAPI();
    }, [fetchIncomeFromAPI]);

    useEffect(() => {
        if (!isLookupsLoading) {
            setDropdownOptions({
                ownerType: getOptions('ownerType'),
                incomeType: getOptions('incomeType'),
                incomeFrequency: getOptions('incomeFrequency'),
                incomeSubType: getOptions('incomeSubType'),
            });
        }
    }, [isLookupsLoading, getOptions]);

    // useEffect(() => {
    //     const typeVal = formData.incomeType;
    //     if (!typeVal) {
    //         setDropdownOptions(prev => ({ ...prev, incomeSubType: [] }));
    //         return;
    //     }
    //     setLoadingSubTypes(true);
    //     const allSubTypes = getOptions('incomeSubType');
    //     const filteredSubs = allSubTypes.filter(sub => sub.parentValue === typeVal);
    //     setDropdownOptions(prev => ({ ...prev, incomeSubType: filteredSubs }));
    //     setLoadingSubTypes(false);
    // }, [formData.incomeType, getOptions]);

    useEffect(() => {
        fetchDependents().then(setDependents).catch(err => console.error(err));
    }, []);
    
    useEffect(() => {
        if (operationStatus.show) {
            const timer = setTimeout(() => setOperationStatus({ show: false, message: '', type: 'success' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [operationStatus]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value, ...(name === 'incomeOwnerType' && { familyMemberId: '' }) }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.incomeOwnerType || !formData.incomeName || !formData.incomeType || !formData.incomeAmount) {
            return alert('Please fill in all required fields marked with *');
        }
        if (formData.incomeOwnerType === '2' && !formData.familyMemberId) {
            return alert('Please select a family member for "Family" income.');
        }
        setIsFormSubmitted(true);
        try {
            await saveOrUpdateIncomeRecord(formData);
            setShowForm(false);
            resetForm();
            await fetchIncomeFromAPI();
            setOperationStatus({ show: true, message: `Income ${editingId ? 'updated' : 'created'} successfully!`, type: 'success' });
        } catch (error: any) {
            setOperationStatus({ show: true, message: error.message || 'Failed to save income.', type: 'error' });
        } finally {
            setIsFormSubmitted(false);
        }
    };
    
    const handleSort = (key: ColumnKey) => {
        let direction: 'asc' | 'desc' = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
        const sortedData = [...transformedData].sort((a, b) => {
            const group = lookupGroupMap[key];
            let aValue: string | number = group ? getLabel(group, a[key as keyof IncomeData] as string) : a[key as keyof IncomeData] ?? '';
            let bValue: string | number = group ? getLabel(group, b[key as keyof IncomeData] as string) : b[key as keyof IncomeData] ?? '';

            if (['incomeAmount', 'totalIncomeAmount', 'totalFamilyIncome'].includes(key)) {
                aValue = parseFloat(aValue as string || '0');
                bValue = parseFloat(bValue as string || '0');
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        setTransformedData(sortedData);
    };

    const handleEdit = (income: IncomeData) => {
        setFormData({ ...income });
        setEditingId(income.id);
        setShowForm(true);
    };
    
    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this income record?')) {
            try {
                await deleteIncomeRecord(id);
                fetchIncomeFromAPI();
                setOperationStatus({ show: true, message: 'Income deleted.', type: 'success' });
            } catch (error: any) {
                setOperationStatus({ show: true, message: error.message || 'Failed to delete income.', type: 'error' });
            }
        }
    };

    const handleRowSelect = (id: string) => {
        setSelectedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
    };
    
    const handleSelectAll = () => {
        if (selectedRows.length === paginatedData.length && paginatedData.length > 0) {
            setSelectedRows([]);
        } else {
            setSelectedRows(paginatedData.map(d => d.id));
        }
    };
    
    const handleBulkDelete = async () => {
        if (window.confirm(`Delete ${selectedRows.length} selected items?`)) {
            try {
                await Promise.all(selectedRows.map(id => deleteIncomeRecord(id)));
                fetchIncomeFromAPI();
                setSelectedRows([]);
                setOperationStatus({ show: true, message: 'Bulk delete successful!', type: 'success' });
            } catch (error: any) {
                setOperationStatus({ show: true, message: error.message || 'Bulk delete failed.', type: 'error' });
            }
        }
    };

    const exportToCSV = () => {
        const headers = visibleColumns.map(col => getColumnDisplayName(col));
        const rows = filteredData.map(income => visibleColumns.map(col => {
            const group = lookupGroupMap[col];
            if (group) return getLabel(group, income[col as keyof IncomeData] as string);
            if (['incomeAmount', 'totalIncomeAmount'].includes(col)) return formatCurrency(income[col as keyof IncomeData] as string);
            return income[col as keyof IncomeData] || 'N/A';
        }));
        const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'income_data.csv';
        link.click();
    };

    const handleImportJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const text = await file.text();
            const importedData = JSON.parse(text).incomes as IncomeData[];
            await Promise.all(importedData.map(inc => saveOrUpdateIncomeRecord(inc)));
            setOperationStatus({ show: true, message: 'Data imported successfully!', type: 'success' });
            fetchIncomeFromAPI();
        } catch (error: any) {
            setOperationStatus({ show: true, message: error.message || 'Failed to import data.', type: 'error' });
        }
    };

    const filteredData = transformedData.filter(income => {
        const getDisplayValue = (colKey: keyof IncomeData) => {
            const group = lookupGroupMap[colKey];
            return group ? getLabel(group, income[colKey] as string) : income[colKey] || 'N/A';
        };
        const searchMatch = searchTerm ? Object.keys(income).some(key =>
            String(getDisplayValue(key as keyof IncomeData)).toLowerCase().includes(searchTerm.toLowerCase())
        ) : true;
        const filterMatch = Object.entries(columnFilters).every(([key, value]) =>
            !value || String(getDisplayValue(key as keyof IncomeData)).toLowerCase().includes(String(value).toLowerCase())
        );
        return searchMatch && filterMatch;
    });

    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    const totalIncome = transformedData.reduce((sum, inc) => sum + parseFloat(inc.totalIncomeAmount || '0'), 0);

    const paginationProps = {
        currentPage, totalPages, totalItems, itemsPerPage, startIndex, endIndex,
        handlePageChange: setCurrentPage,
        handleItemsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
            setItemsPerPage(parseInt(e.target.value));
            setCurrentPage(1);
        },
    };

    const getSortIndicator = (key: ColumnKey) => (sortConfig.key === key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '');
    const handleAddClick = () => { resetForm(); setShowForm(true); };

    return (
        <div className="space-y-8 p-4 sm:p-6">
            <Notification status={operationStatus} onClose={() => setOperationStatus({ ...operationStatus, show: false })} />

            <IncomeHeader
                totalIncome={totalIncome}
                recordCount={filteredData.length}
                apiLoading={apiLoading}
                onAdd={handleAddClick}
                formatCurrency={formatCurrency}
            />

            {showForm && (
                <IncomeForm
                    formData={formData}
                    editingId={editingId}
                    isFormSubmitted={isFormSubmitted}
                    dropdownOptions={dropdownOptions}
                    dependents={dependents}
                    loadingSubTypes={loadingSubTypes}
                    handleSubmit={handleSubmit}
                    handleInputChange={handleInputChange}
                    handleSelectChange={handleSelectChange}
                    onCancel={() => { setShowForm(false); resetForm(); }}
                />
            )}
            
            {/* --- COMPLETE UI IS NOW RENDERED --- */}
            
            <IncomesMobileView
                paginatedData={paginatedData}
                apiLoading={apiLoading}
                searchTerm={searchTerm}
                onSearchChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                getLabel={getLabel}
                formatCurrency={formatCurrency}
                paginationProps={paginationProps}
            />
                 {filteredData.length >0 && (
            <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-x-auto">
                <ActionBar
                    searchTerm={searchTerm}
                    onSearchChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    onBulkDelete={handleBulkDelete}
                    selectedRowCount={selectedRows.length}
                />
                {apiLoading ? (
                      <div className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-green-600" /></div>
                  ) : (
                    <IncomesTable
                        paginatedData={paginatedData}
                        visibleColumns={visibleColumns}
                        getColumnDisplayName={getColumnDisplayName}
                        getSortIndicator={getSortIndicator}
                        handleSort={handleSort}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        selectedRows={selectedRows}
                        handleSelectAll={handleSelectAll}
                        handleRowSelect={handleRowSelect}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        formatCurrency={formatCurrency}
                        getLabel={getLabel}
                        lookupGroupMap={lookupGroupMap}
                    />
                )}
                <Pagination {...paginationProps} />
            </div>
                 )}

            {filteredData.length === 0 && !apiLoading && (
                <EmptyState onAdd={handleAddClick} />
            )}
        </div>
    );
};

export default Income;